import { create } from 'zustand';
import type { CotAffiliation } from '@vtak/shared';

/** An operator-applied classification override for a single track. */
export interface TrackClassification {
  affiliation: CotAffiliation;
  category: string;
}

interface ClassificationState {
  /** Overrides keyed by track uid. */
  byUid: Map<string, TrackClassification>;

  /** Apply (or replace) a track's classification. */
  set: (uid: string, classification: TrackClassification) => void;

  /** Read a track's classification, if any. */
  get: (uid: string) => TrackClassification | undefined;

  /** Remove a track's classification override. */
  clear: (uid: string) => void;
}

const STORAGE_KEY = 'vtak.track-classifications';

/** Operator overrides for track affiliation/category, persisted to localStorage. */
export const useTrackClassificationStore = create<ClassificationState>((set, get) => ({
  byUid: loadFromStorage(),

  set: (uid, classification) =>
    set((state) => persist(withEntry(state.byUid, uid, classification))),

  get: (uid) => get().byUid.get(uid),

  clear: (uid) => set((state) => persist(without(state.byUid, uid))),
}));

/** Return new state with one entry set (immutable). */
function withEntry(
  current: Map<string, TrackClassification>,
  uid: string,
  classification: TrackClassification,
): { byUid: Map<string, TrackClassification> } {
  const next = new Map(current);
  next.set(uid, classification);
  return { byUid: next };
}

/** Return new state with one entry removed (immutable). */
function without(
  current: Map<string, TrackClassification>,
  uid: string,
): { byUid: Map<string, TrackClassification> } {
  const next = new Map(current);
  next.delete(uid);
  return { byUid: next };
}

/** Persist the map to localStorage and return it as state. */
function persist(state: {
  byUid: Map<string, TrackClassification>;
}): { byUid: Map<string, TrackClassification> } {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...state.byUid]));
  } catch {
    /* storage unavailable — keep in-memory only */
  }
  return state;
}

/** Hydrate overrides from localStorage. */
function loadFromStorage(): Map<string, TrackClassification> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Map();
    return new Map(JSON.parse(raw) as [string, TrackClassification][]);
  } catch {
    return new Map();
  }
}
